import type { WorkflowField } from '@/api/workflows';

/**
 * The segment choices offered for a given form field, and how they are labelled.
 *
 * The presets are the point. A government form splits a SIN across three 3-cell
 * combs, and on a 495-field form "Group 2" is one click where "characters 4–6"
 * is arithmetic somebody gets wrong at four in the afternoon. So the common
 * shapes are named, and the raw range stays available for everything else.
 *
 * Kept out of the page component so the choices can be unit-tested without
 * rendering a PDF.
 */
export type SegmentChoice = {
  /** Stored on the mapping. Empty string means "write the whole value". */
  value: string;
  /** i18n key under `pdfMapping.segment`. */
  labelKey: string;
};

const WHOLE: SegmentChoice = { value: '', labelKey: 'whole' };

const DATE_CHOICES: SegmentChoice[] = [
  WHOLE,
  { value: 'year', labelKey: 'year' },
  { value: 'month', labelKey: 'month' },
  { value: 'day', labelKey: 'day' },
];

/**
 * A range that spans the WHOLE value is NOT the same choice as {@link WHOLE},
 * and both belong in the list.
 *
 * `applySegment` strips every non-alphanumeric character before it slices, so
 * `1-10` on a phone the advisor sees as "(416) 555-1234" writes `4165551234`,
 * where "Whole value" writes the masked string verbatim. That difference is
 * what makes a ten-cell comb box — no room for brackets or a dash — fill
 * correctly, and until now it was reachable only by hand-authoring the range,
 * which is why nobody mapping a new form found it.
 */
const SIN_ALL: SegmentChoice = { value: '1-9', labelKey: 'digitsOnly' };
const PHONE_ALL: SegmentChoice = { value: '1-10', labelKey: 'digitsOnly' };
const POSTAL_ALL: SegmentChoice = { value: '1-6', labelKey: 'noSpace' };

/** SIN is always 3-3-3. */
const SIN_CHOICES: SegmentChoice[] = [
  WHOLE,
  SIN_ALL,
  { value: '1-3', labelKey: 'group1' },
  { value: '4-6', labelKey: 'group2' },
  { value: '7-9', labelKey: 'group3' },
];

/** Canadian postal codes split 3-3 (`A1A` `1A1`). */
const POSTAL_CHOICES: SegmentChoice[] = [
  WHOLE,
  POSTAL_ALL,
  { value: '1-3', labelKey: 'group1' },
  { value: '4-6', labelKey: 'group2' },
];

/** North American numbers split area / exchange / line. */
const PHONE_CHOICES: SegmentChoice[] = [
  WHOLE,
  PHONE_ALL,
  { value: '1-3', labelKey: 'areaCode' },
  { value: '4-6', labelKey: 'exchange' },
  { value: '7-10', labelKey: 'line' },
];

/**
 * Segment choices for `field`.
 *
 * Dates are driven by TYPE because the parts are semantic; the rest key off the
 * field's `validate` rule, which is the only reliable signal we have that a
 * ShortText holds a SIN rather than a surname. Anything unrecognised gets the
 * whole value plus whatever custom range the originator types.
 */
export function segmentChoicesFor(
  field: Pick<WorkflowField, 'type' | 'validate'>,
): SegmentChoice[] {
  if (field.type === 'Date') return DATE_CHOICES;
  switch (field.validate) {
    case 'sin':
      return SIN_CHOICES;
    case 'postal':
      return POSTAL_CHOICES;
    case 'phone':
      return PHONE_CHOICES;
    default:
      return [WHOLE];
  }
}

/**
 * True when a raw `from-to` range should be offered alongside the presets —
 * i.e. for everything except dates, whose parts are named and whose character
 * positions would be meaningless to address directly.
 */
export function allowsCustomRange(field: Pick<WorkflowField, 'type'>): boolean {
  return field.type !== 'Date';
}

/**
 * Label for a segment that is not one of the presets, so a hand-authored or
 * seeded range still reads as something rather than blank.
 */
export function customRangeLabel(segment: string): string {
  return segment;
}

/**
 * How a handle JOINS the box another column already fills — `segment`'s mirror.
 *
 * A short preset list rather than a free-text box on purpose: the separator sits
 * between two values inside one PDF box, and the realistic answers are a slash,
 * a comma, a dash or a plain space. Free text here invites a typo nobody sees
 * until a signed form comes back with `BMO Money Market Fund//1234`.
 */
export type JoinChoice = {
  /** Stored on the mapping. Empty string means "replace the box" (the default). */
  value: string;
  /** i18n key under `pdfMapping.join`. */
  labelKey: string;
};

export const JOIN_CHOICES: JoinChoice[] = [
  { value: '', labelKey: 'replace' },
  { value: ' / ', labelKey: 'slash' },
  { value: ', ', labelKey: 'comma' },
  { value: ' - ', labelKey: 'dash' },
  { value: ' ', labelKey: 'space' },
];

/**
 * The same separators offered to an ADDENDUM — which already appends by
 * definition, so the list differs from {@link JOIN_CHOICES} in exactly two ways:
 * there is no "replace" entry (an addendum cannot replace), and the empty string
 * means "run the two values together" rather than "do not append".
 *
 * Space leads because the case the feature exists for is two answers sharing one
 * printed line — a dealer name and its code — and that is what the fill path
 * falls back to when a mapping carries no separator of its own.
 */
export const ADDENDUM_JOIN_CHOICES: JoinChoice[] = [
  { value: ' ', labelKey: 'space' },
  { value: ' / ', labelKey: 'slash' },
  { value: ', ', labelKey: 'comma' },
  { value: ' - ', labelKey: 'dash' },
  { value: '', labelKey: 'none' },
];

/** What the fill path appends with when an addendum carries no `joinWith`.
 *  Mirrors `ADDENDUM_SEPARATOR` in `form-core/collectFilledFields.ts`, so the
 *  dropdown shows the separator that will actually be used. */
export const ADDENDUM_DEFAULT_JOIN = ' ';
